import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { getRouteApi, Link } from '@tanstack/react-router'
import { ChevronLeftIcon, ChevronRightIcon, DownloadIcon, SearchIcon, XIcon } from 'lucide-react'
import { useMemo } from 'react'
import { CariLink } from '@app/components/entity-link'
import { ScreenHeader } from '@app/components/screen-header'
import { Button } from '@app/components/ui/button'
import { Card } from '@app/components/ui/card'
import { Input } from '@app/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableNumCell,
  TableRow,
  TableScroller,
} from '@app/components/ui/table'
import { cn } from '@app/lib/cn'
import { useCsvDownload } from '@app/lib/download'
import { useUrlSearchInput } from '@app/lib/nav'
import { purchasingApi, purchasingQueryKeys } from '@app/features/satinalma/api'
import type {
  PurchaseFilter,
  PurchaseListRow,
  PurchaseListSummary,
} from '@app/features/satinalma/api'
import { formatAmount, formatDate, formatQty } from '@app/features/satinalma/format'
import { SupplierBoard } from '@app/features/satinalma/supplier-board'
import {
  DeliveryDateCell,
  EmptyState,
  ErrorState,
  ReceiptBadge,
  ReceiptBar,
  StatusBadge,
  TableSkeleton,
} from '@app/features/satinalma/ui'

/**
 * Satınalma — the purchasing list.
 *
 * Two halves: the supplier board on top answers "who still owes us goods", the
 * order table below answers "what exactly, and how much of it has arrived".
 * Both load immediately — no filter form to fill in first, unlike Korgun.
 *
 * Default order is open work first, and late work first within that, so the 61
 * orders somebody still has to chase sit at the top of page one.
 *
 * Search, status chip, the selected supplier tile and the page all live in the
 * URL (see `validateSearch` in router.tsx), so "the three suppliers I was just
 * chasing" survives a refresh and can be pasted into a message.
 */

const route = getRouteApi('/_korumali/satinalma')

const PAGE_SIZE = 50

/**
 * Column priority.
 *
 * The sidebar takes 224px from `md` up, leaving the table ~570px at 834 and
 * ~936px at 1200. What survives everywhere is the question the screen exists to
 * answer — who, how much is still outstanding, and what state it is in. What it
 * was (Malzemeler) and what it costs (Tutar) return as soon as there is room;
 * Gelen is dropped first because Gelen = sipariş − kalan.
 */
const COLUMN = {
  date: 'hidden xl:table-cell',
  /** TeslimTar is filled on only 66 of 563 POs — mostly an empty column. */
  delivery: 'hidden 2xl:table-cell',
  materials: 'hidden lg:table-cell',
  received: 'hidden 2xl:table-cell',
  receipt: 'hidden sm:table-cell',
  /* 1024 is too narrow for the amount once Malzemeler is back; 1140 is not. */
  amount: 'hidden min-[1140px]:table-cell',
} as const

const STATUS_CHIPS: { value: PurchaseFilter; label: string; key: keyof PurchaseListSummary }[] = [
  { value: 'all', label: 'Tümü', key: 'all' },
  { value: 'open', label: 'Açık', key: 'open' },
  { value: 'overdue', label: 'Geciken', key: 'overdue' },
  { value: 'pending', label: 'Bekleyen', key: 'pending' },
  { value: 'partial', label: 'Kısmi gelen', key: 'partial' },
  { value: 'complete', label: 'Tamamlanan', key: 'complete' },
  { value: 'closed', label: 'Kapalı', key: 'closed' },
]

export function PurchasingScreen() {
  const search = route.useSearch()
  const navigate = route.useNavigate()
  const csv = useCsvDownload()

  const query = search.query ?? ''
  const status = search.status ?? 'all'
  const page = search.page ?? 1
  /** Set when the filter came from a click on the supplier board, for the active state. */
  const boardSupplier = search.supplier ?? null

  /*
   * The box keeps its own copy so typing never waits for a navigation, and
   * lands in the URL 300 ms later, `replace` so one search is one history entry.
   * Typing also drops the board selection: the tile is no longer what is driving
   * the list.
   */
  const [searchInput, setSearchInput, flushSearch] = useUrlSearchInput(query, (text) => {
    void navigate({
      search: (prev) => ({
        ...prev,
        query: text === '' ? undefined : text,
        supplier: undefined,
        page: undefined,
      }),
      replace: true,
      // The commit fires mid-typing — a scroll reset would yank the viewport
      // away from the box (see the üretim list for the full story).
      resetScroll: false,
    })
  })

  const params = useMemo(
    () => ({ query, status, page, pageSize: PAGE_SIZE }),
    [query, status, page],
  )

  const { data, isPending, isError, error, isFetching, refetch } = useQuery({
    queryKey: purchasingQueryKeys.list(params),
    queryFn: () => purchasingApi.list(params),
    placeholderData: keepPreviousData,
  })

  const summaryQuery = useQuery({
    queryKey: purchasingQueryKeys.summary(),
    queryFn: () => purchasingApi.summary(),
  })

  const rows = data?.rows ?? []
  const summary = data?.summary
  const total = data?.total ?? 0
  const totalPages = data?.totalPages ?? 1
  // With `keepPreviousData` the response still holds the OLD page while the next
  // one loads, so the range label is read from the response, not local state.
  const serverPage = data?.page ?? page
  const serverPageSize = data?.pageSize ?? PAGE_SIZE
  const first = total === 0 ? 0 : (serverPage - 1) * serverPageSize + 1
  const last = Math.min(serverPage * serverPageSize, total)
  const hasFilters = query !== '' || status !== 'all'

  function clearFilters() {
    // Same reason as `selectSupplier`: the URL is the single writer, the box follows.
    void navigate({
      search: (prev) => ({
        ...prev,
        query: undefined,
        status: undefined,
        supplier: undefined,
        page: undefined,
      }),
      resetScroll: false,
    })
  }

  /** Page 1 is the default, so it leaves the URL entirely. */
  function goToPage(next: number) {
    void navigate({ search: (prev) => ({ ...prev, page: next <= 1 ? undefined : next }) })
  }

  /**
   * Board click → the list's own search filter, so the two stay in one state.
   * `supplier` rides along in the URL purely to keep the tile lit after a
   * refresh; the actual filtering is the cari code in `query`.
   */
  function selectSupplier(cariKod: string) {
    if (boardSupplier === cariKod) {
      clearFilters()
      return
    }
    /*
     * The box is NOT set here. Writing it would start the search debounce, and
     * that commit clears `supplier` — the tile lit up and went dark again ~300ms
     * later. Setting only the URL lets the box follow it, with nothing pending.
     */
    void navigate({
      search: (prev) => ({
        ...prev,
        query: cariKod,
        supplier: cariKod,
        status: 'open',
        page: undefined,
      }),
      resetScroll: false,
    })
  }

  return (
    <>
      <ScreenHeader
        title="Satınalma"
        description="Alış siparişleri, mal kabul durumu ve tedarikçi bazında açık yük."
        actions={
          <span className="font-mono text-micro tnum text-ink-muted">
            {isPending ? 'yükleniyor…' : `${formatQty(total)} sipariş`}
          </span>
        }
      />

      {/* ── Supplier board: who still owes us goods ── */}
      <SupplierBoard
        data={summaryQuery.data}
        isPending={summaryQuery.isPending}
        activeSupplier={boardSupplier}
        onSelect={selectSupplier}
      />

      <Card className="mt-3">
        <div className="flex flex-col gap-2.5 border-b border-hairline px-3.5 py-2.5">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-52 flex-1 sm:max-w-sm">
              <SearchIcon
                className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-ink-muted"
                aria-hidden
              />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Tedarikçi, sipariş no, belge no ya da malzeme"
                aria-label="Satınalma siparişlerinde ara"
                className="pl-7"
              />
              {searchInput ? (
                <button
                  type="button"
                  onClick={() => setSearchInput('')}
                  aria-label="Aramayı temizle"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-xs p-0.5 text-ink-muted hover:text-ink"
                >
                  <XIcon className="size-3.5" aria-hidden />
                </button>
              ) : null}
            </div>

            {/* Exports the whole filtered PO list, not just the page on screen. */}
            <Button
              variant="outline"
              disabled={csv.busy}
              aria-busy={csv.busy}
              onClick={() => csv.start('/satinalma/export', { query: flushSearch(), status })}
            >
              <DownloadIcon aria-hidden />
              {csv.busy ? 'Hazırlanıyor…' : 'Dışa aktar'}
            </Button>

            {csv.error ? (
              <span role="alert" className="text-micro text-hardal">
                {csv.error}
              </span>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-1">
            {STATUS_CHIPS.map((chip) => {
              const active = status === chip.value
              const count = summary?.[chip.key]
              return (
                <button
                  key={chip.value}
                  type="button"
                  onClick={() => {
                    // Keeps the viewport still and carries text waiting on the
                    // search debounce. A pending text change drops the supplier
                    // tile, mirroring the search commit's own rule.
                    const text = flushSearch({ commit: false })
                    void navigate({
                      search: (prev) => ({
                        ...prev,
                        query: text === '' ? undefined : text,
                        supplier: text === query ? prev.supplier : undefined,
                        status: chip.value === 'all' ? undefined : chip.value,
                        page: undefined,
                      }),
                      resetScroll: false,
                    })
                  }}
                  aria-pressed={active}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-xs border px-2 py-1 text-micro transition-colors',
                    active
                      ? 'border-kordovan/30 bg-kordovan/8 font-medium text-kordovan'
                      : 'border-hairline bg-surface text-ink-muted hover:border-ink-muted/40 hover:text-ink',
                  )}
                >
                  {chip.label}
                  <span
                    className={cn('font-mono tnum', active ? 'text-kordovan' : 'text-ink-muted/70')}
                  >
                    {count === undefined ? '·' : formatQty(count)}
                  </span>
                </button>
              )
            })}
          </div>
        </div>

        <TableScroller className={cn(isFetching && !isPending && 'opacity-60 transition-opacity')}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-20">Sipariş</TableHead>
                <TableHead className="min-w-36">Tedarikçi</TableHead>
                <TableHead className={cn('w-24', COLUMN.date)}>Tarih</TableHead>
                <TableHead className={cn('w-28', COLUMN.delivery)}>Teslim</TableHead>
                <TableHead className={cn('min-w-44', COLUMN.materials)}>Malzemeler</TableHead>
                <TableHead className={cn('w-20 text-right', COLUMN.received)}>Gelen</TableHead>
                <TableHead className="w-18 text-right">Kalan</TableHead>
                <TableHead className={cn('w-28', COLUMN.receipt)}>Mal kabul</TableHead>
                <TableHead className={cn('w-28 text-right', COLUMN.amount)}>Tutar</TableHead>
                <TableHead className="w-20">Durum</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {isPending ? (
                <TableSkeleton rows={10} cols={10} />
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={10}>
                    <ErrorState
                      message={
                        error instanceof Error ? error.message : 'Bilinmeyen bir hata oluştu.'
                      }
                      onRetry={() => void refetch()}
                    />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10}>
                    <EmptyState
                      title="Bu filtreyle satınalma siparişi yok"
                      description={
                        hasFilters
                          ? 'Aramayı kısaltıp ya da durum filtresini kaldırıp yeniden bakın.'
                          : 'Alış siparişi defteri boş görünüyor. Korgun aynası güncel mi kontrol edin.'
                      }
                      action={
                        hasFilters ? (
                          <Button variant="primary" onClick={clearFilters}>
                            Filtreleri temizle
                          </Button>
                        ) : null
                      }
                    />
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.sipNo} className="relative">
                    <TableCell className="font-mono">
                      {/* The whole row is the link — no record arrows like Korgun. */}
                      <Link
                        to="/satinalma/$sipNo"
                        params={{ sipNo: String(row.sipNo) }}
                        className="font-medium text-ink after:absolute after:inset-0 after:content-[''] hover:text-kordovan"
                      >
                        {row.sipNo}
                      </Link>
                    </TableCell>

                    {/* max-w-0: without it the nowrap inside `truncate` sets the
                        column's minimum to the longest supplier name and the whole
                        table goes into horizontal scroll. */}
                    <TableCell className="max-w-0">
                      {/* Sits above the row-link overlay so the cari card stays
                          reachable — but only while it IS a link: printed as
                          plain text for an account without cari, it drops back
                          under the overlay and the row opens the order again. */}
                      <CariLink
                        ckod={row.supplierKod}
                        title="Cari kartını aç"
                        className="relative z-1 block truncate font-medium text-ink hover:text-kordovan"
                        plainClassName="block truncate font-medium text-ink"
                      >
                        {row.supplierName}
                      </CariLink>
                      <span className="block truncate font-mono text-micro text-ink-muted">
                        {row.supplierKod}
                        {row.documentNo ? ` · ${row.documentNo}` : ''}
                      </span>
                    </TableCell>

                    <TableCell className={cn('font-mono tnum text-ink-muted', COLUMN.date)}>
                      {formatDate(row.orderDate)}
                    </TableCell>

                    <TableCell className={COLUMN.delivery}>
                      <DeliveryDateCell
                        dateText={formatDate(row.deliveryDate)}
                        overdueDays={row.overdueDays}
                      />
                    </TableCell>

                    <TableCell className={cn('max-w-0', COLUMN.materials)}>
                      <MaterialsCell row={row} />
                    </TableCell>

                    <TableNumCell className={cn('text-ink-muted', COLUMN.received)}>
                      {formatQty(row.receivedQty)}
                      <span className="text-ink-muted/60"> / {formatQty(row.orderedQty)}</span>
                    </TableNumCell>

                    <TableNumCell
                      className={cn(row.kalanQty > 0 ? 'font-medium text-ink' : 'text-ink-muted/60')}
                    >
                      {row.kalanQty > 0 ? formatQty(row.kalanQty) : '—'}
                    </TableNumCell>

                    <TableCell className={COLUMN.receipt}>
                      <ReceiptBar
                        percent={row.percent}
                        status={row.receiptStatus}
                        overdue={row.overdue}
                      />
                    </TableCell>

                    <TableNumCell className={COLUMN.amount}>
                      <AmountCell row={row} />
                    </TableNumCell>

                    {/* Never leaves: "açık ve geciken" is the reason to open this
                        screen. Badges stack rather than widen the column. */}
                    <TableCell>
                      <span className="flex flex-col items-start gap-0.5">
                        <StatusBadge status={row.status} />
                        <ReceiptBadge status={row.receiptStatus} overdue={row.overdue} />
                      </span>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableScroller>

        {!isPending && !isError && rows.length > 0 ? (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-hairline px-3.5 py-2">
            <span className="font-mono text-micro tnum text-ink-muted">
              {formatQty(first)}–{formatQty(last)} / {formatQty(total)}
            </span>
            <div className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => goToPage(page - 1)}
              >
                <ChevronLeftIcon aria-hidden />
                Önceki
              </Button>
              <span className="px-1 font-mono text-micro tnum text-ink-muted">
                {formatQty(serverPage)} / {formatQty(totalPages)}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => goToPage(Math.min(totalPages, page + 1))}
              >
                Sonraki
                <ChevronRightIcon aria-hidden />
              </Button>
            </div>
          </div>
        ) : null}
      </Card>

      <p className="mt-2 text-micro text-ink-muted">
        Gelen miktarlar Korgun'un <code className="font-mono">Si_GCHar</code> defterinden gelir; alış
        siparişlerinde giren/çıkan sütunları ters çalıştığı için yön düzeltilmiştir (çıkan =
        sipariş, giren = gelen). Teslim tarihi 563 siparişin yalnızca 66'sında girilmiş, bu yüzden
        "geciken" sayısı yalnızca tarihi olan siparişleri kapsar.
      </p>
    </>
  )
}

/**
 * What was ordered, in one line. The name comes from the stock card —
 * `Siparis_Har.Tanim` is a free-text note ("86860 RAVINI"), not the material.
 */
function MaterialsCell({ row }: { row: PurchaseListRow }) {
  if (row.materials.length === 0) {
    return <span className="text-micro text-ink-muted/70">Satır girilmemiş</span>
  }
  const rest = row.materialCount - row.materials.length
  return (
    <span className="block min-w-0">
      <span className="block truncate text-data text-ink" title={row.materials.join(' · ')}>
        {row.materials.join(' · ')}
        {rest > 0 ? <span className="text-ink-muted"> +{formatQty(rest)}</span> : null}
      </span>
      <span className="block font-mono text-micro tnum text-ink-muted">
        {formatQty(row.lineCount)} satır · {formatQty(row.orderedQty)} sipariş
      </span>
    </span>
  )
}

/**
 * Amount cell.
 *
 * 9 of the 563 purchase orders carry different currencies across their lines and
 * have no single total: the breakdown is stacked under the TL equivalent. Most
 * orders are simply unpriced, which renders as an em dash rather than "0,00".
 */
function AmountCell({ row }: { row: PurchaseListRow }) {
  if (row.mixedCurrency) {
    const breakdown = row.currencyTotals.filter((p) => p.amount !== 0)
    return (
      <span
        className="flex flex-col items-end leading-tight"
        title="Sipariş satırlarında birden fazla para birimi var; toplamlar ayrı ayrı gösterilir."
      >
        <span>{row.amountTl === null ? '—' : formatAmount(row.amountTl, 'TL')}</span>
        <span className="text-micro text-hardal">
          {breakdown.length > 0
            ? breakdown.map((p) => formatAmount(p.amount, p.currency)).join(' + ')
            : 'karışık para birimi'}
        </span>
      </span>
    )
  }

  if (row.amount === null || row.amount === 0) {
    return <span className="text-ink-muted/60" title="Bu siparişte fiyat girilmemiş">
      —
    </span>
  }

  return <span>{formatAmount(row.amount, row.currency)}</span>
}
